import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Person as PersonIcon,
  Security as SecurityIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';
import { useTranslation } from 'react-i18next';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  isActive?: boolean;
}

interface PermissionTemplate {
  id: string;
  template_code: string;
  template_name: string;
  description: string;
}

interface Permission {
  id: string;
  permission_code: string;
  module: string;
  action: string;
  description: string;
}

const PermissionManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user, token, hasPermission } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [templates, setTemplates] = useState<PermissionTemplate[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Dialog states
  const [assignTemplateDialog, setAssignTemplateDialog] = useState<{ open: boolean; userId: string | null }>({
    open: false,
    userId: null
  });
  const [assignPermissionDialog, setAssignPermissionDialog] = useState<{ open: boolean; userId: string | null }>({
    open: false,
    userId: null
  });
  const [viewPermissionsDialog, setViewPermissionsDialog] = useState<{ open: boolean; userId: string | null }>({
    open: false,
    userId: null
  });
  
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [selectedPermissionId, setSelectedPermissionId] = useState<string>('');
  const [userPermissionsMap, setUserPermissionsMap] = useState<Record<string, { permissions: string[]; templates: PermissionTemplate[] }>>({});
  const [showArchived, setShowArchived] = useState(false); // Hide archived users by default

  useEffect(() => {
    if (hasPermission('admin.permissions')) {
      fetchData();
    }
  }, [hasPermission, user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch users, templates, and permissions in parallel
      const [usersRes, templatesRes, permissionsRes] = await Promise.all([
        fetch(getApiUrl('/users'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(getApiUrl('/permissions/templates'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(getApiUrl('/permissions'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      if (usersRes.ok) {
        const usersData = await usersRes.json();
        const usersList = usersData.users || [];
        setUsers(usersList);
        // Permission fetches use full list; display is filtered by showArchived below

        // Fetch permissions for all users
        const userPermsPromises = usersList.map(async (u: User) => {
          try {
            const permRes = await fetch(getApiUrl(`/permissions/users/${u.id}`), {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            if (permRes.ok) {
              const permData = await permRes.json();
              return { userId: u.id, data: permData };
            }
          } catch (err) {
            console.error(`Error fetching permissions for user ${u.id}:`, err);
          }
          return null;
        });

        const userPermsResults = await Promise.all(userPermsPromises);
        const permsMap: Record<string, { permissions: string[]; templates: PermissionTemplate[] }> = {};
        userPermsResults.forEach(result => {
          if (result) {
            permsMap[result.userId] = {
              permissions: result.data.permissions || [],
              templates: result.data.templates || []
            };
          }
        });
        setUserPermissionsMap(permsMap);
      }

      if (templatesRes.ok) {
        const templatesData = await templatesRes.json();
        setTemplates(templatesData.templates || []);
      }

      if (permissionsRes.ok) {
        const permissionsData = await permissionsRes.json();
        setPermissions(permissionsData.permissions || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignTemplate = async () => {
    if (!assignTemplateDialog.userId || !selectedTemplateId) return;

    try {
      const response = await fetch(
        getApiUrl(`/permissions/users/${assignTemplateDialog.userId}/templates/${selectedTemplateId}`),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        setSuccess(t('permissions.management.errors.templateAssignedSuccessfully'));
        const assignedUserId = assignTemplateDialog.userId;
        setAssignTemplateDialog({ open: false, userId: null });
        setSelectedTemplateId('');
        // Refresh permissions for the specific user who had template assigned
        if (assignedUserId) {
          try {
            const permRes = await fetch(getApiUrl(`/permissions/users/${assignedUserId}`), {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            if (permRes.ok) {
              const permData = await permRes.json();
              setUserPermissionsMap(prev => ({
                ...prev,
                [assignedUserId]: {
                  permissions: permData.permissions || [],
                  templates: permData.templates || []
                }
              }));
            }
          } catch (err) {
            console.error(`Error refreshing permissions for user ${assignedUserId}:`, err);
          }
        }
        // Also refresh all data to ensure consistency
        fetchData();
      } else {
        const data = await response.json();
        setError(data.message || t('permissions.management.errors.failedToAssignTemplate'));
      }
    } catch (err) {
      console.error('Error assigning template:', err);
      setError(t('permissions.management.errors.failedToAssignTemplate'));
    }
  };

  const handleRemoveTemplate = async (userId: string, templateId: string) => {
    if (!window.confirm('Are you sure you want to remove this template?')) return;

    try {
      const response = await fetch(
        getApiUrl(`/permissions/users/${userId}/templates/${templateId}`),
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        setSuccess('Template removed successfully');
        fetchData();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to remove template');
      }
    } catch (err) {
      console.error('Error removing template:', err);
      setError('Failed to remove template');
    }
  };

  const handleAssignPermission = async () => {
    if (!assignPermissionDialog.userId || !selectedPermissionId) return;

    try {
      const response = await fetch(
        getApiUrl(`/permissions/users/${assignPermissionDialog.userId}/permissions/${selectedPermissionId}`),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        setSuccess(t('permissions.management.errors.permissionGrantedSuccessfully'));
        setAssignPermissionDialog({ open: false, userId: null });
        setSelectedPermissionId('');
        fetchData();
      } else {
        const data = await response.json();
        setError(data.message || t('permissions.management.errors.failedToGrantPermission'));
      }
    } catch (err) {
      console.error('Error granting permission:', err);
      setError(t('permissions.management.errors.failedToGrantPermission'));
    }
  };

  const handleViewPermissions = async (userId: string) => {
    try {
      const response = await fetch(getApiUrl(`/permissions/users/${userId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserPermissionsMap(prev => ({
          ...prev,
          [userId]: {
            permissions: data.permissions || [],
            templates: data.templates || []
          }
        }));
        setViewPermissionsDialog({ open: true, userId });
      }
    } catch (err) {
      console.error('Error fetching user permissions:', err);
      setError(t('permissions.management.errors.failedToLoadUserPermissions'));
    }
  };

  if (!hasPermission('admin.permissions')) {
    return (
      <Box p={3}>
        <Alert severity="error">
          {t('permissions.management.noAccess')}
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" mb={3}>
        <SecurityIcon sx={{ mr: 2, fontSize: 32, color: '#1976d2' }} />
        <Typography variant="h4" component="h1">
          {t('permissions.management.title')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Typography variant="h6">
              {t('permissions.management.usersAndPermissions')}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  color="primary"
                />
              }
              label={t('permissions.management.showArchived', 'Rodyti archyvuotus')}
            />
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('permissions.management.user')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('permissions.management.assignedTemplates')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('permissions.management.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(showArchived ? users : users.filter((u) => u.isActive !== false)).map((u) => {
                  const userData = userPermissionsMap[u.id];
                  const userTemplates = userData?.templates || [];
                  
                  return (
                    <TableRow key={u.id} sx={u.isActive === false ? { backgroundColor: 'action.hover' } : undefined}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <span>{u.firstName} {u.lastName}</span>
                          {u.isActive === false && (
                            <Chip label={t('common.archived', 'Archived')} size="small" color="default" variant="outlined" />
                          )}
                        </Box>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {u.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {userTemplates.map(t => (
                            <Chip
                              key={t.id}
                              label={t.template_name || 'Unknown'}
                              size="small"
                              onDelete={() => handleRemoveTemplate(u.id, t.id)}
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={t('permissions.management.viewPermissions')}>
                          <IconButton
                            size="small"
                            onClick={() => handleViewPermissions(u.id)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('permissions.management.assignTemplate')}>
                          <IconButton
                            size="small"
                            onClick={() => setAssignTemplateDialog({ open: true, userId: u.id })}
                          >
                            <AddIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('permissions.management.grantPermission')}>
                          <IconButton
                            size="small"
                            onClick={() => setAssignPermissionDialog({ open: true, userId: u.id })}
                          >
                            <SecurityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Assign Template Dialog */}
      <Dialog open={assignTemplateDialog.open} onClose={() => setAssignTemplateDialog({ open: false, userId: null })}>
        <DialogTitle>{t('permissions.management.assignTemplateDialog.title')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, minWidth: 300 }}>
            <InputLabel>{t('permissions.management.assignTemplateDialog.templateLabel')}</InputLabel>
            <Select
              value={selectedTemplateId}
              label={t('permissions.management.assignTemplateDialog.templateLabel')}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
            >
              {templates.map(t => (
                <MenuItem key={t.id} value={t.id}>
                  {t.template_name} - {t.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignTemplateDialog({ open: false, userId: null })}>{t('common.cancel')}</Button>
          <Button onClick={handleAssignTemplate} variant="contained" disabled={!selectedTemplateId}>
            {t('permissions.management.assignTemplateDialog.assign')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Permission Dialog */}
      <Dialog open={assignPermissionDialog.open} onClose={() => setAssignPermissionDialog({ open: false, userId: null })}>
        <DialogTitle>{t('permissions.management.grantPermissionDialog.title')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, minWidth: 300 }}>
            <InputLabel>{t('permissions.management.grantPermissionDialog.permissionLabel')}</InputLabel>
            <Select
              value={selectedPermissionId}
              label={t('permissions.management.grantPermissionDialog.permissionLabel')}
              onChange={(e) => setSelectedPermissionId(e.target.value)}
            >
              {permissions.map(p => (
                <MenuItem key={p.id} value={p.id}>
                  {p.permission_code} - {p.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignPermissionDialog({ open: false, userId: null })}>{t('common.cancel')}</Button>
          <Button onClick={handleAssignPermission} variant="contained" disabled={!selectedPermissionId}>
            {t('permissions.management.grantPermissionDialog.grant')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Permissions Dialog */}
      <Dialog 
        open={viewPermissionsDialog.open} 
        onClose={() => setViewPermissionsDialog({ open: false, userId: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('permissions.management.viewPermissionsDialog.title')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>{t('permissions.management.viewPermissionsDialog.assignedTemplates')}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {userPermissionsMap[viewPermissionsDialog.userId || '']?.templates.map(t => (
                <Chip key={t.id} label={t.template_name} />
              )) || <Typography color="text.secondary">{t('permissions.management.viewPermissionsDialog.noTemplatesAssigned')}</Typography>}
            </Box>

            <Typography variant="h6" gutterBottom>{t('permissions.management.viewPermissionsDialog.effectivePermissions')}</Typography>
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {userPermissionsMap[viewPermissionsDialog.userId || '']?.permissions.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('permissions.management.viewPermissionsDialog.permissionCode')}</TableCell>
                      <TableCell>{t('permissions.management.viewPermissionsDialog.module')}</TableCell>
                      <TableCell>{t('permissions.management.viewPermissionsDialog.action')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userPermissionsMap[viewPermissionsDialog.userId || '']?.permissions.map((permCode, idx) => {
                      const perm = permissions.find(p => p.permission_code === permCode);
                      return (
                        <TableRow key={idx}>
                          <TableCell>{permCode}</TableCell>
                          <TableCell>{perm?.module || '-'}</TableCell>
                          <TableCell>{perm?.action || '-'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <Typography color="text.secondary">{t('permissions.management.viewPermissionsDialog.noPermissionsAssigned')}</Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewPermissionsDialog({ open: false, userId: null })}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PermissionManagement;

